import type { ReactNode } from 'react';
import { Database, Eye, ShieldCheck } from 'lucide-react';
import { ParaconLogo } from '@/components/brand/ParaconLogo';

const FEATURES = [
  { icon: Database, label: 'Centralised business information' },
  { icon: ShieldCheck, label: 'Secure role-based access' },
  { icon: Eye, label: 'Clear project and compliance visibility' },
] as const;

/**
 * Split-panel authentication chrome.
 *
 * The navy panel is decorative and hides below `lg`, where the card takes the
 * full width and the brand is reduced to a wordmark above it.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen bg-cloud">
      {/* Brand panel — desktop only */}
      <aside className="relative hidden w-auth-panel shrink-0 flex-col justify-between overflow-hidden bg-navy px-16 py-14 lg:flex">
        <div aria-hidden className="absolute inset-0 bg-auth-grid bg-auth-grid" />

        <div className="relative">
          <ParaconLogo />
        </div>

        <div className="relative">
          <h1 className="max-w-[440px] font-display text-display text-white">
            One connected platform for construction projects, people and compliance
          </h1>
          <p className="mt-6 max-w-[430px] text-[15px] leading-relaxed text-white/65">
            Manage projects, people, compliance and business operations from one secure system.
          </p>

          <ul className="mt-12 space-y-6">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/25 text-white">
                  <Icon aria-hidden className="h-[18px] w-[18px]" />
                </span>
                <span className="text-[15px] text-white/85">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative flex items-center gap-3 text-2xs font-semibold uppercase tracking-[0.12em] text-white/45">
          <ShieldCheck aria-hidden className="h-4 w-4" />
          Authorised Paracon users only
        </p>
      </aside>

      {/* Form column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
          <div className="w-full max-w-[440px]">
            <div className="mb-8 flex justify-center lg:hidden">
              <ParaconLogo tone="dark" />
            </div>
            <div className="rounded-xl border border-line bg-white p-6 shadow-card sm:p-8">{children}</div>
          </div>
        </div>

        <footer className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 px-4 pb-8 text-helper text-muted">
          <a href="/privacy" className="transition-colors hover:text-body">
            Privacy
          </a>
          <span aria-hidden className="text-muted/40">
            ·
          </span>
          <a href="/security" className="transition-colors hover:text-body">
            Security
          </a>
          <span aria-hidden className="text-muted/40">
            ·
          </span>
          <a href="/support" className="transition-colors hover:text-body">
            Support
          </a>
          <span aria-hidden className="text-muted/40">
            ·
          </span>
          <span>© {new Date().getFullYear()} Paracon</span>
        </footer>
      </div>
    </div>
  );
}

/** Eyebrow + heading + supporting copy, shared by all three auth cards. */
export function AuthHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
}) {
  return (
    <div className="mb-6">
      <p className="text-2xs font-semibold uppercase tracking-[0.1em] text-muted">{eyebrow}</p>
      <h2 className="mt-2 font-display text-[28px] font-bold leading-tight text-ink">{title}</h2>
      {description && <p className="mt-2.5 text-sm leading-relaxed text-muted">{description}</p>}
    </div>
  );
}
